import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { UserService } from "./user.service";
import { EventService } from "./event.service";
import { Router } from "@angular/router";
import { LogService } from "./log.service";
import { environment } from './../../../src/environments/environment';
import { Store, select } from '@ngrx/store';
import { JoiEvent } from '../store/events.era';
import { Dictionary } from '@ngrx/entity';
import { ViewEventDialogComponent } from '../components/events/dialog.viewEvent.component';
import { JoiAccount } from '../../../globals/store/accounts.data';
import { MatDialog } from '@angular/material/dialog';
import { openDialogGetResult } from './../helpers/angular-helpers';
import { EditEventDialogComponent } from '../modules/dashboard/dashboard.dialog.editEvent.component';
import { DuplicateEventDialogComponent } from '../modules/dashboard/dashboard.dialog.duplicateEvent.component';
import { ProgramService } from './program.service';
import { ChangeEventAccountDialogComponent } from '../modules/dashboard/dashboard.dialog.changeEventAccount.component';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../components/dialog.confirmDelete.component';
import { User } from '../../../globals/store/user.data';
import { WorkforceService } from './workforce.service';
import { CloseEventDialogComponent } from '../modules/dashboard/dashboard.dialog.closeEvent.component';
import { LabelMakerData, LabelMakerDialogComponent, LabelUpdateFunctionParams } from '../components/label-marker/label-maker.dialog.component';
import { AccountsService } from './accounts.service';
import { getAccountEventLabels } from '../store/reselectors/accounts.reselector';
import { formatDate } from '@angular/common';
import { DAY_MS } from './../helpers/constants';
import { EventStatus } from 'globals/store/events.data';
import { EditEventStatusDialogComponent } from '../modules/dashboard/dashboard.dialog.editEventStatus.component';
import { accountsFeature } from '../store/accounts.era';
import { userFeature } from '../store/user.era';
import { subTilDestroy } from '../helpers/helpers';
import { jsonCopyObject, unknownDateToDate } from 'globals/helpers/helpers';
import { uiActions } from '../store/ui.era';

/**
  I am extracting the view event logic here for these reasons:
  1) To be able to use shared functionality between event view and calendars
  2) To slim down event service a bit and calendar / event view
  3) To ensure there are no circular dependencies for what it needs to do (which might happen in event service)
*/
@Injectable({ providedIn: 'root' })
export class EventViewService implements OnDestroy {

  readonly api = `${environment.functionsURL}api/app`;
  public $destroy: Subject<boolean> = new Subject();
  private _file = 'event.view.service.ts'
  public _accounts: Dictionary<JoiAccount> = {} // not meant to be accessed outside but public to work with subTilDestroy
  public _user: User

  //
  constructor(public userService: UserService,
    private router: Router,
    private eventService: EventService,
    private programService: ProgramService,
    private accountService: AccountsService,
    private workforceService: WorkforceService,
    private log: LogService,
    public store: Store
  ) {

    subTilDestroy(this, select(accountsFeature.selectEntities), '_accounts')
    subTilDestroy(this, select(userFeature.selectUser), '_user')

  }

  public eventDates(evt: JoiEvent, all_date = false): string { // originally only used in event view / home dashboard
    const d_arr = evt ? evt.dates_string : [];
    if (!d_arr) {
      return ''
    }
    if (all_date) {
      return d_arr.join(' ')
    } else {
      return d_arr.length === 4 ? '' : d_arr.join(' ')
    }
  }

  private getDaysTil(date): number {
    if (!date) return 0;
    const d: number = unknownDateToDate(date).getTime() + DAY_MS
    const days = Math.floor((d - Date.now()) / 1000 / 60 / 60 / 24);
    return days;
  }

  public daysUntil(evt: JoiEvent): string {
    if (evt.dates && evt.dates.length) {
      const until_first_day = typeof evt.dates[0] === 'string' ? this.getDaysTil(new Date(evt.dates[0])) : this.getDaysTil(evt.dates[0]);
      const until_last_day = typeof evt.dates[0] === 'string' ? this.getDaysTil(new Date(evt.dates[evt.dates.length - 1])) : this.getDaysTil(evt.dates[evt.dates.length - 1]);
      // console.log({ evt, until_first_day, until_last_day })
      if (until_first_day > 0) {
        return until_first_day + (until_first_day === 1 ? ' day' : ' days');
      } else if (until_last_day < 0) {
        return 'past';
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  async editEventTitleThroughDialog(event: JoiEvent, dialog: MatDialog) {
    const result: any = await openDialogGetResult(dialog, EditEventDialogComponent, {
      width: '480px', disableClose: true, data: { event_name: event.title }
    })
    if (!result || event.title === result.event_name) return
    await this.eventService.updateTitle(event, result.event_name)
  }

  async editEventStatusThroughDialog(event: JoiEvent, dialog: MatDialog) {
    const eventStatus = await openDialogGetResult<EventStatus>(dialog, EditEventStatusDialogComponent, {
      width: '480px', disableClose: true, data: event
    })
    if (!eventStatus) return
    await this.eventService.updateEvent(event.eventId, { eventStatus })
  }
  
  
  async closeEventThroughDialog(event: JoiEvent, dialog: MatDialog) {
    const close: any = await openDialogGetResult(dialog, CloseEventDialogComponent, { width: '480px', disableClose: true, autoFocus: false })
    if (!close) return
    this.eventService.closeEvent(jsonCopyObject(event))
  }  

  async duplicateEventThroughDialog(event: JoiEvent, dialog: MatDialog) {
    const result: any = await openDialogGetResult(dialog, DuplicateEventDialogComponent, { width: '480px', disableClose: true, autoFocus: false, data:{event:event} })
    if (!result) return
    this.store.dispatch(uiActions.updateUIItem('takeover_loading', 'event'))
    // const start = performance.now()
    const eventId = await this.eventService.duplicateEvent(event, result.event_name, result.use_account_id, result.new_date)
    if (!eventId) return // error handled within event service, returns nothing
    await this.router.navigate(['event/build/'+eventId])
    this.programService.storeProgramDates(true, true)
    this.store.dispatch(uiActions.updateUIItem('takeover_loading', null))
  }

  async changeEventAccountThroughDialog(event: JoiEvent, dialog: MatDialog) {
    const result: any = await openDialogGetResult(dialog, ChangeEventAccountDialogComponent, { width: '480px', disableClose: true, autoFocus: false, data:{ event } })
    if (result && result.id) {
      this.eventService.changeEventAccount(event.eventId, result.id);
    }
  }

  async deleteEventThroughPositionedDialog(event: JoiEvent, dialog: MatDialog, rect?: DOMRect) {
    const data: ConfirmDeleteData = { rect, title: 'Delete this event', dialog: dialog }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.eventService.deleteEvent(event.eventId)
  }

  async leaveWorkforceThroughPositionedDialog(event: JoiEvent, dialog: MatDialog, rect?: DOMRect) {
    const data: ConfirmDeleteData = { rect, title: 'Leave the workforce from this event', dialog, deleteButtonText: 'LEAVE', para: this._accounts[event.accountId]?.is_manager ? null : 'Since you are not on the account, you will no longer be able to see this event if you leave it.\n\nThis action cannot be undone.' }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    console.log('SHOULD BE LEAVING WORKFORCE FOR ' + event.title + '! (but nothing is happening)')
    this.workforceService.removeFromEvent(this._user, event, false)
  }

  private labelUpdateFunctionGenerator(event: JoiEvent) {
    const that = this
    const labelUpdateFunction = ({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) => {  // GH- needs event to be passed because there is no selected event on the home page
      const { accountId, eventId } = event
      if (labelsToggled) {
        // TO DO: DISPATCH THROUGH STORE?
        that.eventService.updateEvent(eventId, { labels })
      }
      if (labelsChanged) {
        that.accountService.updateAccount(
          accountId,
          { labels: labelTemplate },
          { file: 'event-view.component.ts', function: 'labelUpdateFunction', error: new Error(), message: 'There was an error adding this label' })
      }
    }
    return labelUpdateFunction
  }

  // Add or edit labels
  async addLabelThroughPositionedDialog(event: JoiEvent, dialog: MatDialog, rect?: DOMRect) {
    const account = this._accounts[event.accountId]
    const { accountEventLabelsList } = getAccountEventLabels(account)

    const data: LabelMakerData = {
      labels: event.labels || [],
      type: 'event',
      position: !rect ? null : { top: rect.top, left: rect.left - 300 },
      labelUpdateFunction: this.labelUpdateFunctionGenerator(jsonCopyObject(event)),
      labelTemplate: accountEventLabelsList,
    }
    
    openDialogGetResult(dialog, LabelMakerDialogComponent, {
      minWidth: '300px',
      backdropClass: 'backdrop',
      panelClass: 'label-panel',
      maxHeight: '99vh',
      data
    })

  }


  async openViewEventDialog(event: JoiEvent, dialog: MatDialog, viewFromEvent = false) {
    const data: ViewEventData = {
      event,
      daysUntil: this.daysUntil(event),
      dates: this.eventDates(event, true),
      isManager: this._accounts[event.accountId] && this._accounts[event.accountId].is_manager,
      viewFromEvent,
    }
    const result = await openDialogGetResult(dialog, ViewEventDialogComponent, { width: '480px', disableClose: true, data })
    if (!result) return

    switch(result) {
      case 'join':
        this.eventService.grantWorkforcePrivileges(event).catch(response => {
          this.log.error({file:this._file, function:'grantWorkforcePrivileges',response,message:'There was an error granting workforce permissions',error:new Error(), data:{ event }})
        })
        break;
      case 'edit_title':
        this.editEventTitleThroughDialog(event, dialog)
        break;
      case 'view':
        this.eventService.openingEvent(null, event)
        break;
      case 'label':
        this.addLabelThroughPositionedDialog(event, dialog)
        break;
      case 'duplicate':
        this.duplicateEventThroughDialog(event, dialog)
        break;
      case 'change':
        this.changeEventAccountThroughDialog(event, dialog)
        break;
      case 'close':
        this.closeEventThroughDialog(event, dialog)
        break;
      case 'delete':
        this.deleteEventThroughPositionedDialog(event, dialog)
        break;
    }
  }

  ngOnDestroy() {
    this.$destroy.next(true); this.$destroy.complete();
  }
}
export interface ViewEventData {
  event: JoiEvent,
  daysUntil: string,
  dates: string,
  isManager: boolean
  viewFromEvent?: boolean
}
